import { useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Cambios del manejador en curso, todavía sin escribir en la URL.
 *
 * Hace falta porque un manejador cambia dos cosas a la vez —el filtro y
 * regresar a la página 1— y el `setSearchParams` del router resuelve cada
 * asignación contra la URL de ese momento, no contra la que otra asignación
 * acaba de pedir: sin acumular, la segunda pisa a la primera y el filtro nunca
 * llega a la barra.
 *
 * Se vacía en el microtask siguiente, o sea en cuanto el manejador termina, así
 * que solo vive durante ese instante y no guarda estado entre pantallas.
 */
let acumulado: URLSearchParams | null = null

/**
 * Estado de un listado que vive en la URL en vez de en memoria.
 *
 * Es para los filtros, el orden y la página de un listado al que se vuelve
 * desde un detalle. Con `useState` no sobreviven: al volver, la pantalla se
 * monta de nuevo y quien había filtrado y paginado pierde todo y vuelve a
 * empezar. En la URL aguantan el ir y venir y el refresh, y de paso el listado
 * filtrado queda compartible por link.
 *
 * Dos decisiones que importan:
 *
 * El valor por omisión no se escribe. Si no, la barra se llena de parámetros
 * vacíos (`?buscar=&proyecto=&pagina=1`) y un listado sin filtrar se ve como si
 * tuviera algo aplicado.
 *
 * Los cambios reemplazan la entrada del historial en lugar de apilarse. Sin
 * eso, el botón atrás tendría que deshacer letra por letra lo que se escribió
 * en el buscador antes de salir de la pantalla.
 *
 * Se puede usar varias veces en el mismo manejador —cambiar un filtro y
 * regresar a la página 1, por ejemplo— porque cada asignación actualiza sobre
 * los parámetros anteriores y no sobre una copia ya vieja.
 */
export function useEstadoEnUrl<T extends string | number | null>(
  clave: string,
  porOmision: T,
): [T, (valor: T | ((previo: T) => T)) => void] {
  const [params, setParams] = useSearchParams()

  /**
   * Convierte el texto de la URL al tipo del valor por omisión.
   *
   * Va aparte porque se lee en dos momentos: al renderizar y al resolver un
   * cambio en forma funcional. Si el segundo se saltara esta conversión, un
   * `setPagina(p => p + 1)` sumaría sobre el texto y daría "31" en vez de 4.
   */
  const leer = useCallback(
    (crudo: string | null): T => {
      if (crudo === null) return porOmision
      if (typeof porOmision === 'number') {
        const numero = Number(crudo)
        // Un parámetro escrito a mano puede traer basura; ahí manda el valor
        // por omisión en lugar de dejar pasar un NaN al resto de la pantalla.
        return (Number.isFinite(numero) ? numero : porOmision) as T
      }
      return crudo as T
    },
    [porOmision],
  )

  const valor = leer(params.get(clave))

  // La base del lote sale de aqui y no de window.location: la ubicacion global
  // no es la verdad cuando el router es de memoria o de hash, y aun con el de
  // navegador puede ir un paso atras a media transicion. Con la ref siempre se
  // parte de los parametros que el router tiene ahora.
  const paramsRef = useRef(params)
  paramsRef.current = params

  const asignar = useCallback(
    (nuevo: T | ((previo: T) => T)) => {
      const arrancaLote = acumulado === null
      if (acumulado === null) {
        acumulado = new URLSearchParams(paramsRef.current)
      }
      const siguientes = acumulado

      // La forma funcional se resuelve contra lo acumulado, no contra lo que se
      // leyó al renderizar, para que un `setOrden(prev => invertir(prev))`
      // alterne bien aunque se llame junto con otro cambio.
      const resuelto: T =
        typeof nuevo === 'function'
          ? (nuevo as (previo: T) => T)(leer(siguientes.get(clave)))
          : nuevo

      if (resuelto === porOmision || resuelto === '' || resuelto === null) {
        siguientes.delete(clave)
      } else {
        siguientes.set(clave, String(resuelto))
      }

      if (arrancaLote) {
        queueMicrotask(() => {
          const final = acumulado
          acumulado = null
          if (final) setParams(final, { replace: true })
        })
      }
    },
    [clave, leer, porOmision, setParams],
  )

  return [valor, asignar]
}

export default useEstadoEnUrl
